/** biome-ignore-all lint/correctness/useUniqueElementIds: Mye enklere å holde orden på lenkene enn masse variabler? */
import { Heading, Link, Paragraph } from "@digdir/designsystemet-react";
import { ExternalLinkIcon } from "@navikt/aksel-icons";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { Page } from "@/components";
import { formatDate } from "@/utils";

const LinkWithFootnote = ({
  id,
  children,
}: PropsWithChildren<{
  id: number;
}>) => (
  <Link href={`#fn${id}`} id={`ref${id}`} aria-describedby={`fn${id}`}>
    {children}
    <sup>{id}</sup>
  </Link>
);
const LinkWithReference = ({
  id,
  children,
}: PropsWithChildren<{ id: number }>) => {
  return (
    <li id={`fn${id}`}>
      {children}{" "}
      <a href={`#ref${id}`} aria-label="Tilbake til referansen">
        ↩
      </a>
    </li>
  );
};

export const VilkårPage = () => {
  const { t } = useTranslation();
  // Vilkår siden er gjengitt direkte slik de er i vilkårene og er ikke oversatt.
  return (
    <main className="container mx-auto grid grid-cols-1 xl:grid-cols-[1fr_auto] py-14 gap-6 gap-x-12 max-w-page">
      <Page
        withBackLink
        className="flex flex-col gap-4 leading-9 max-w-4xl! mx-0"
      >
        <Heading level={1} data-size="xl" className="mb-6 text-3xl md:text-5xl">
          Vilkår for fjernvarmekunden ved bestilling av Norgespris
        </Heading>
        <section className="max-w-card flex flex-col gap-6">
          <Paragraph>Dagens dato {formatDate(new Date())}</Paragraph>

          <Paragraph className="whitespace-pre-line">
            Ved å bestille Norgespris, bekrefter du/dere å ha lest og godtar
            vilkårene for Norgespris. Vilkårene følger av{" "}
            <LinkWithFootnote id={1}>
              lov om Norgespris og strømstønad til husholdninger
            </LinkWithFootnote>
            , og{" "}
            <LinkWithFootnote id={2}>
              forskrift om Norgespris og strømstønad til husholdninger som
              bruker fjernvarme
            </LinkWithFootnote>
            . Her kan du lese vilkårene:
          </Paragraph>
        </section>

        <section>
          <Heading level={2} data-size="xs">
            Deltakelse
          </Heading>
          <ul>
            <li>
              Du/dere må ha rett til å delta i ordningen
              <LinkWithFootnote id={3} />. Disse kundegruppene har rett til å
              delta
              <LinkWithFootnote id={4} />:
              <ul>
                <li>
                  Fjernvarmekunde med husholdningsforbruk som er kunde:
                  <ul>
                    <li>
                      av fjernvarmeselskap med konsesjon etter{" "}
                      <LinkWithFootnote id={5} />, eller
                    </li>
                    <li>
                      av nærvarmeselskap som registrerer seg hos NVE.
                      <LinkWithFootnote id={6} />
                    </li>
                  </ul>
                </li>
                <li>
                  Fjernvarmekunde med fritidsboligforbruk som er kunde:
                  <ul>
                    <li>
                      av fjernvarmeselskap med konsesjon etter energiloven,
                      eller
                    </li>
                    <li>av nærvarmeselskap som registrerer seg hos NVE.</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              Du/dere er klar over at dere mister rett til strømstønad så lenge
              avtalen om Norgespris er gyldig, hvis du/dere har rett til
              strømstønad.
              <LinkWithFootnote id={7} />
            </li>
          </ul>
        </section>

        <section>
          <Heading level={2} data-size="xs">
            Pris
          </Heading>
          <ul>
            <li>
              Du/dere er klar over at referanseprisen i inneværende prisperiode,
              er 50 øre per kWh inklusiv merverdiavgift (40 øre per kWh
              eksklusiv merverdiavgift).
              <LinkWithFootnote id={8} />
            </li>
          </ul>
        </section>

        <section>
          <Heading level={2} data-size="xs">
            Opplysningsplikt
          </Heading>
          <ul>
            <li>
              Du/dere plikter å opplyse om antall boenheter ved bestilling
              dersom du/dere er en fjernvarmekunde med fellesmålt
              fjernvarmeforbruk.
              <LinkWithFootnote id={9} />
            </li>
            <li>
              Du/dere plikter ved bestillingen å opplyse om andel oppvarmet
              areal til annet forbruk, herunder næringsforbruk, som utgjør mer
              enn 20 prosent av totalt oppvarmet areal.
              <LinkWithFootnote id={10} /> Dette gjelder dersom du/dere er en
              fjernvarmekunde med fellesmålt fjernvarmeforbruk.
            </li>
          </ul>
        </section>

        <section>
          <Heading level={2} data-size="xs">
            Binding og avbestilling
          </Heading>
          <ul>
            <li>
              Du/dere er innforstått med at gjennomført bestilling av Norgespris
              binder energimåleren din/deres fra første dag i påfølgende måned
              fra bestillingsdato og ut inneværende prisperiode.
              <LinkWithFootnote id={11} /> Bindingstiden gjelder med mindre
              avbestillingsretten blir benyttet innenfor fristen.
            </li>
            <li>
              Du/dere har som kunde en avbestillingsrett av Norgespris.
              Avbestillingsretten gjelder i 14 dager fra bestillingsdato.
              <LinkWithFootnote id={12} />{" "}
              <span>
                Dersom Norgespris avbestilles innen 14 dager, vil retten til
                prissikring falle bort for de dagene før du/dere har benyttet
                avbestillingsretten.
              </span>
            </li>
          </ul>
        </section>

        <section>
          <Heading level={2} data-size="xs">
            Betaling
          </Heading>
          <ul>
            <li>
              Du/dere er innforstått med at fjernvarmeselskapet kan kreve inn og
              betale ut prissikringsbeløp. <LinkWithFootnote id={13} />
              <ul>
                <li>
                  Hvorvidt fjernvarmeselskapet krever inn eller betaler ut
                  prissikringsbeløp, avhenger av hva som er gjennomsnittlig
                  månedspris på strøm (elspotpris) den aktuelle måneden
                  sammenlignet med referanseprisen.
                </li>

                <li>
                  Fjernvarmeselskapet skal beregne prissikringsbeløp per måned
                  basert på målt fjernvarmeforbruk og referansepris. Beregningen
                  skal skje på følgende måte:
                  <ul>
                    <li>
                      I måneder der elspotprisen er over referanseprisen skal
                      kunden få et tillegg på fakturaen som tilsvarer
                      elspotprisen minus referanseprisen.
                    </li>

                    <li>
                      I måneder der elspotprisen er under referanseprisen skal
                      kunden betale inn et beløp som tilsvarer referanseprisen
                      minus elspotprisen.
                    </li>
                  </ul>
                </li>

                <li>
                  Dersom fjernvarmeselskapet har fakturert kunden feil
                  prissikringsbeløp, skal feilen korrigeres på første mulige
                  faktura.
                  <LinkWithFootnote id={14} />
                </li>
              </ul>
            </li>
          </ul>
        </section>

        <section>
          <Heading level={2} data-size="xs">
            Forbrukstak
          </Heading>
          <ul>
            <li>
              Du/dere vil ikke få beregnet prissikringsbeløp for forbruk utover
              forbrukstaket.
              <LinkWithFootnote id={15} /> Forbrukstaket er:
              <ul>
                <li>4 500 kWh per måned per energimåler for husholdning.</li>
                <li>4 500 kWh per måned per boenhet i boligselskap.</li>
                <li>1 000 kWh per måned per energimåler for fritidsbolig.</li>
                <li>
                  4 500 kWh per måned per energimåler for fjernvarmekunde som
                  bor fast i fritidsbolig og har egen energimåler.
                </li>
              </ul>
            </li>
            <li>
              Er du fjernvarmekunde som bor fast i fritidsbolig, må følgende
              krav være oppfylt for å ha rett på et forbrukstak på 4 500 kWh per
              måned per energimåler: Du har sendt fjernvarmeselskapet et gyldig
              vedtak fra kommunen om varig eller midlertidig
              bruksendringstillatelse til boligformål, eller det fremgår av
              Folkeregisteret at du er og har vært bostedsregistrert på
              nåværende adresse senest 9. juni 2025.
            </li>
          </ul>
        </section>

        <section>
          <Heading level={2} data-size="xs">
            Fordeling av prissikringsbeløp
          </Heading>
          <ul>
            <li>
              Du/dere plikter å sørge for at prissikringsbeløpet fordeles på
              boenheter med husholdningsforbruk eller fritidsboligforbruk dersom
              du/dere er en fjernvarmekunde med fellesmålt fjernvarmeforbruk.
              <LinkWithFootnote id={16} />
            </li>
          </ul>
        </section>

        <section>
          <Heading level={2} data-size="xs">
            Rapportering
          </Heading>
          <ul>
            <li>
              Du/dere plikter å rapportere inn hvis mer enn 20 prosent av
              oppvarmet areal omfattes av annet forbruk, herunder
              næringsforbruk, dersom du/dere er et boligselskap med fellesmålt
              fjernvarmeforbruk.
              <LinkWithFootnote id={17} />
              <ul>
                <li>
                  Denne rapporteringsplikten gjelder generelt for boligselskap
                  som mottar Norgespris eller strømstønad. Rapporteringen skal
                  angi hvor stor andel av oppvarmet areal som omfattes av annet
                  forbruk.
                </li>
              </ul>
            </li>
          </ul>
        </section>

        <section>
          <Heading level={2} data-size="xs">
            Tilsyn og kontroll
          </Heading>
          <ul>
            <li>
              Du/dere er klar over at Norges vassdrags- og energidirektorat
              (NVE) fører tilsyn med ordningen.
              <LinkWithFootnote id={18} />
            </li>
          </ul>
        </section>

        <section>
          <Heading
            level={2}
            data-size="lg"
            className="hidden"
            aria-hidden="false"
          >
            Fotnoter for vilkår
          </Heading>
          <ol className="text-sm">
            <LinkWithReference id={1}>
              <Link
                href={t("eksterneLenker.lovOmNorgespris.url")}
                target="_blank"
                rel="noreferrer"
              >
                Lov 20. juni 2025 nr. 44 om Norgespris og strømstønad til
                husholdninger.
                <ExternalLinkIcon fontSize={"1.5rem"} className="pl-1" />
              </Link>
            </LinkWithReference>
            <LinkWithReference id={2}>
              <Link
                href={t("eksterneLenker.forskriftOmNorgespris.url")}
                target="_blank"
                rel="noreferrer"
              >
                Forskrift 8. september 2025 om Norgespris og strømstønad til
                husholdninger som bruker fjernvarme.
                <ExternalLinkIcon fontSize={"1.5rem"} className="pl-1" />
              </Link>
            </LinkWithReference>
            <LinkWithReference id={3}>jf. forskriften § 14.</LinkWithReference>
            <LinkWithReference id={4}>jf. forskriften § 13.</LinkWithReference>
            <LinkWithReference id={5}>Se energiloven § 5-1.</LinkWithReference>
            <LinkWithReference id={6}>Se forskriften § 24.</LinkWithReference>
            <LinkWithReference id={7}>
              jf. forskriften § 13 tredje ledd.
            </LinkWithReference>
            <LinkWithReference id={8}>jf. forskriften §19.</LinkWithReference>
            <LinkWithReference id={9}>
              jf. forskriften § 14 annet ledd.
            </LinkWithReference>
            <LinkWithReference id={10}>
              jf. forskriften § 14 annet ledd.
            </LinkWithReference>
            <LinkWithReference id={11}>
              jf. forskriften § 18 og loven § 15 første ledd annet punktum.
            </LinkWithReference>
            <LinkWithReference id={12}>jf. forskriften § 17.</LinkWithReference>
            <LinkWithReference id={13}>
              jf. forskriften § 20 og loven §15.
            </LinkWithReference>
            <LinkWithReference id={14}>jf. forskriften § 29.</LinkWithReference>
            <LinkWithReference id={15}>jf. forskriften § 21.</LinkWithReference>
            <LinkWithReference id={16}>jf. forskriften § 25.</LinkWithReference>
            <LinkWithReference id={17}>jf. forskriften § 26.</LinkWithReference>
            <LinkWithReference id={18}>
              jf. forskriften § 33 og loven §§ 4 og 23.
            </LinkWithReference>
          </ol>
        </section>
      </Page>
    </main>
  );
};
